import { useMemo, useState, useEffect } from "react";
import { useLang } from "@/lib/i18n";
import Icon from "@/components/shared/Icon";

const SYMBOLS = [
  { icon: "Cat", color: "#FF91B8" },
  { icon: "Star", color: "#FFC928" },
  { icon: "Cloud", color: "#147DEB" },
  { icon: "Leaf", color: "#45C878" },
  { icon: "Sun", color: "#FFC928" },
  { icon: "BookOpen", color: "#147DEB" },
  { icon: "Heart", color: "#FF91B8" },
  { icon: "Rocket", color: "#45C878" },
];

function shuffle(a) {
  const arr = [...a];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function MemoryMatch({ onFinish, difficulty = "easy" }) {
  const { t, code } = useLang();
  const pairs = difficulty === "hard" ? 8 : difficulty === "medium" ? 6 : 4;

  const deck = useMemo(() => {
    const chosen = SYMBOLS.slice(0, pairs);
    const cards = shuffle([...chosen, ...chosen].map((s, i) => ({ ...s, key: i })));
    return cards;
  }, [pairs]);

  const [flipped, setFlipped] = useState([]); // indices currently face-up (unmatched)
  const [matched, setMatched] = useState([]); // matched indices
  const [moves, setMoves] = useState(0);
  const [lock, setLock] = useState(false);

  useEffect(() => {
    if (flipped.length === 2) {
      setLock(true);
      const [a, b] = flipped;
      const same = deck[a].icon === deck[b].icon;
      const tmo = setTimeout(() => {
        if (same) setMatched((m) => [...m, a, b]);
        setFlipped([]);
        setLock(false);
      }, 650);
      return () => clearTimeout(tmo);
    }
  }, [flipped, deck]);

  useEffect(() => {
    if (matched.length > 0 && matched.length === deck.length) {
      const tmo = setTimeout(() => onFinish(pairs, pairs, moves), 400);
      return () => clearTimeout(tmo);
    }
  }, [matched, deck.length, pairs, moves, onFinish]);

  const flip = (i) => {
    if (lock || flipped.includes(i) || matched.includes(i)) return;
    if (flipped.length === 0) setFlipped([i]);
    else {
      setFlipped((f) => [...f, i]);
      setMoves((m) => m + 1);
    }
  };

  const cols = pairs <= 4 ? "grid-cols-4" : pairs <= 6 ? "grid-cols-4" : "grid-cols-4";

  return (
    <div>
      <div className="flex items-center justify-between text-sm font-bold text-[#64748B]">
        <span>{code === "zh" ? "翻牌数" : "Moves"}: {moves}</span>
        <span className="inline-flex items-center gap-1.5 text-[#147DEB]"><Icon name="Check" className="h-4 w-4 text-[#45C878]" /> {matched.length / 2} / {pairs}</span>
      </div>
      <div className={`mt-5 grid ${cols} gap-3`}>
        {deck.map((card, i) => {
          const isUp = flipped.includes(i) || matched.includes(i);
          const isMatched = matched.includes(i);
          return (
            <button
              key={card.key}
              onClick={() => flip(i)}
              data-testid={`memory-card-${i}`}
              aria-label={isUp ? card.icon : "hidden card"}
              className={`aspect-square rounded-2xl border-2 transition-all duration-200 ${
                isUp
                  ? isMatched
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-[#147DEB]/30 bg-white"
                  : "border-[#147DEB]/20 bg-gradient-to-br from-[#147DEB] to-[#0F62BD] hover:-translate-y-0.5"
              } flex items-center justify-center`}
            >
              {isUp ? (
                <Icon name={card.icon} className="h-7 w-7 sm:h-9 sm:w-9" style={{ color: card.color }} />
              ) : (
                <Icon name="Sparkles" className="h-6 w-6 text-white/60" />
              )}
            </button>
          );
        })}
      </div>
      <p className="mt-4 text-center text-sm text-[#64748B]">{t("play.subtitle").slice(0, 0)}{code === "zh" ? "找出所有配对即可完成！" : "Find all matching pairs to finish!"}</p>
    </div>
  );
}
